import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { api, apiErrorMessage } from '../lib/api'

const input =
  'mb-3 w-full rounded-md border border-b4l-border px-3 py-2 text-[13px] outline-none focus:border-b4l-green'
const buton =
  'w-full rounded-md bg-b4l-green py-2 text-[13px] font-medium text-white transition-colors hover:bg-b4l-green-dark disabled:opacity-60'

function Cadru({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-b4l-bg px-4">
      <div className="w-full max-w-sm rounded-xl border border-b4l-border bg-white p-6 shadow-sm">
        <h1 className="mb-1 text-center text-lg font-semibold text-b4l-green-dark">eMAG Platform</h1>
        {children}
      </div>
    </div>
  )
}

/**
 * Două ecrane într-unul, după cum vine sau nu un jeton în adresă:
 *   fără jeton → ceri legătura pe e-mail
 *   cu jeton   → alegi parola nouă
 */
export function ResetareParolaPage() {
  const [params] = useSearchParams()
  const jeton = params.get('jeton') ?? ''
  return jeton ? <ParolaNoua jeton={jeton} /> : <CereLegatura />
}

function CereLegatura() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [trimis, setTrimis] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setLoading(true)
    try {
      await api.post('/auth/forgot-password', { email })
      // Mesajul e ACELAȘI fie că adresa există sau nu — altfel pagina ar spune cine are cont.
      setTrimis(true)
    } catch (err) {
      toast.error(apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  if (trimis) {
    return (
      <Cadru>
        <p className="mb-4 text-center text-[12px] text-b4l-text-dim">Am uitat parola</p>
        <div className="mb-4 rounded-md bg-b4l-bg-soft p-4 text-[13px] leading-snug text-b4l-text">
          Dacă adresa există în platformă, ai primit un e-mail cu legătura de resetare.
          <p className="mt-2 text-[12px] text-b4l-text-dim">
            Legătura e valabilă 60 de minute și poate fi folosită o singură dată. Dacă nu vezi mesajul,
            uită-te și în „Spam".
          </p>
        </div>
        <Link to="/login" className="block text-center text-[12px] text-b4l-green hover:underline">
          Înapoi la autentificare
        </Link>
      </Cadru>
    )
  }

  return (
    <Cadru>
      <p className="mb-5 text-center text-[12px] text-b4l-text-dim">Am uitat parola</p>
      <form onSubmit={submit}>
        <p className="mb-3 text-[12px] leading-snug text-b4l-text-muted">
          Scrie adresa cu care intri în platformă. Îți trimitem o legătură pe e-mail, cu care îți alegi
          o parolă nouă.
        </p>
        <label className="mb-1 block text-[12px] text-b4l-text-muted">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
          className={input}
        />
        <button type="submit" disabled={loading} className={buton}>
          {loading ? 'Se trimite…' : 'Trimite legătura'}
        </button>
      </form>
      <Link to="/login" className="mt-4 block text-center text-[12px] text-b4l-green hover:underline">
        Înapoi la autentificare
      </Link>
    </Cadru>
  )
}

function ParolaNoua({ jeton }: { jeton: string }) {
  const nav = useNavigate()
  const [parola, setParola] = useState('')
  const [confirmare, setConfirmare] = useState('')
  const [loading, setLoading] = useState(false)

  const preaScurta = parola.length > 0 && parola.length < 10
  const nuCoincid = confirmare.length > 0 && parola !== confirmare

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (preaScurta || nuCoincid) return
    setLoading(true)
    try {
      await api.post('/auth/reset-password', { jeton, password: parola })
      toast.success('Parolă schimbată. Intră cu ea acum.')
      nav('/login')
    } catch (err) {
      toast.error(apiErrorMessage(err), { duration: 10000 })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Cadru>
      <p className="mb-5 text-center text-[12px] text-b4l-text-dim">Alege o parolă nouă</p>
      <form onSubmit={submit}>
        <label className="mb-1 block text-[12px] text-b4l-text-muted">Parolă nouă</label>
        <input
          type="password"
          value={parola}
          onChange={(e) => setParola(e.target.value)}
          required
          autoFocus
          className={input}
        />
        <label className="mb-1 block text-[12px] text-b4l-text-muted">Încă o dată</label>
        <input
          type="password"
          value={confirmare}
          onChange={(e) => setConfirmare(e.target.value)}
          required
          className={input}
        />
        {preaScurta && <p className="mb-3 text-[12px] text-b4l-red">Cel puțin 10 caractere.</p>}
        {nuCoincid && <p className="mb-3 text-[12px] text-b4l-red">Cele două nu coincid.</p>}
        <button type="submit" disabled={loading || preaScurta || nuCoincid} className={buton}>
          {loading ? 'Se salvează…' : 'Salvează parola'}
        </button>
      </form>
      <p className="mt-4 text-center text-[11px] text-b4l-text-dim">
        Toate sesiunile deschise se închid, pe orice dispozitiv.
      </p>
    </Cadru>
  )
}
